import { fetcher, useAuthFetch } from "matsuri-hooks"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    Storage,
    validateSelectableStorageId
} from "../domain/inventory/storage"
import { m2mCore_v1 } from "../endpoints/m2m-core.v1"

export const useStorages = (
    token: string,
    { includeArchived = false }: { includeArchived?: boolean } = {}
) => {
    return useAuthFetch<Storage[]>(
        token,
        m2mCore_v1.findStoragesByCompanyId(
            includeArchived ? { includeArchived: "true" } : {}
        ),
        {}
    )
}

/*
 * Storageをstateとして扱い、storageの初期値を草加倉庫にしたい場合に利用してください。
 * ただし、defaultStorageIdが指定されている場合は、その値を初期値として利用します。
 */
export const useStorageIdState = (token: string, defaultStorageId?: string) => {
    const { data: storages, isLoading } = useStorages(token)

    const [storageId, setStorageId] = useState(() => {
        if (defaultStorageId) {
            return defaultStorageId
        }
        const storage = storages?.find(storage => storage.name === "草加倉庫")
        return storage?.id ?? ""
    })

    const fired = useRef(defaultStorageId ? true : false)

    // storagesが後から取得された場合、初期値を更新する
    // defaultStorageIdが渡されている場合は、実行しない
    useEffect(() => {
        if (
            fired.current === false &&
            isLoading === false &&
            storageId === ""
        ) {
            const storage = storages?.find(
                storage => storage.name === "草加倉庫"
            )
            fired.current = true
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setStorageId(storage?.id ?? "")
        }
    }, [isLoading, storageId, storages])

    return {
        storageId,
        setStorageId,
        isLoading
    }
}

// 単一の倉庫を取得する。アーカイブ済みの倉庫でも名前解決や詳細表示ができるよう、
// アーカイブ済みを含めた一覧から取得する。
export const useStorage = (token: string, storageId: string) => {
    const { data, ...others } = useStorages(token, { includeArchived: true })

    const storage = useMemo(
        () => data?.find(storage => storage.id === storageId),
        [data, storageId]
    )

    return { data: storage, ...others }
}

export const requestFindStorages = (token: string) => {
    return fetcher<Storage[]>(
        m2mCore_v1.findStoragesByCompanyId({ includeArchived: "true" }),
        { token }
    )
}

/*
 * CSVインポートの行ごとの検証で利用する。倉庫一覧の取得は初回のみ行い、以降は使い回す。
 */
export const useSelectableStorageIdValidator = (token: string) => {
    const storagesRequest = useRef<
        ReturnType<typeof requestFindStorages> | undefined
    >(undefined)

    return useCallback(
        async (storageId: string): Promise<string | undefined> => {
            if (storageId === "") {
                return undefined
            }

            if (!storagesRequest.current) {
                storagesRequest.current = requestFindStorages(token)
            }

            const { data: storages, error } = await storagesRequest.current
            if (error || !storages) {
                storagesRequest.current = undefined
                return "倉庫の検索に失敗しました"
            }

            return validateSelectableStorageId(storages, storageId)
        },
        [token]
    )
}

export const requestCreateStorage = (
    token: string,
    input: { name: string }
) => {
    const endpoint = m2mCore_v1.createStorage

    return fetcher(endpoint(), {
        token,
        method: endpoint.method,
        body: JSON.stringify(input)
    })
}

export const requestUpdateStorage = (
    token: string,
    storageId: string,
    input: { name: string }
) => {
    const endpoint = m2mCore_v1.updateStorage

    return fetcher(endpoint({ id: storageId }), {
        token,
        method: endpoint.method,
        body: JSON.stringify(input)
    })
}
